import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import PageHeader from '@/components/shared/PageHeader';
import {
  Camera, Edit2, Save, X, Award, Package, ClipboardList,
  ShieldCheck, Calendar, Mail, Phone, Building2, Briefcase, Settings,
} from 'lucide-react';

const MOCK_SKILLS = ['R2 Certified', 'NAID AAA', 'Data Sanitization', 'HDD Shredding', 'Battery Handling', 'DOT Hazmat'];

const MOCK_ACHIEVEMENTS = [
  { label: 'Assets Processed', value: '1,240', icon: Package, color: 'text-primary' },
  { label: 'Work Orders Completed', value: '89', icon: ClipboardList, color: 'text-chart-2' },
  { label: 'Days Without Incident', value: '45', icon: ShieldCheck, color: 'text-chart-3' },
  { label: 'Certifications Earned', value: '6', icon: Award, color: 'text-chart-4' },
];

export default function ProfilePage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);

  const [form, setForm] = useState({
    name: user?.name ?? '',
    email: user?.email ?? '',
    phone: '(555) 123-4567',
    title: 'Operations Manager',
    department: 'Processing',
  });

  const initials = (user?.name ?? 'U')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleSave = () => {
    setEditing(false);
    toast({ title: 'Profile updated', description: 'Your changes have been saved.' });
  };

  const handleCancel = () => {
    setForm({
      name: user?.name ?? '',
      email: user?.email ?? '',
      phone: '(555) 123-4567',
      title: 'Operations Manager',
      department: 'Processing',
    });
    setEditing(false);
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader title="My Profile" subtitle="Manage your personal information and view achievements" />

      {/* Hero */}
      <Card>
        <CardContent className="flex flex-col items-center gap-4 p-6 sm:flex-row sm:items-start">
          <div className="relative">
            <Avatar className="h-24 w-24 text-2xl">
              <AvatarImage src="" />
              <AvatarFallback className="bg-primary/10 text-primary">{initials}</AvatarFallback>
            </Avatar>
            <Button
              variant="outline"
              size="icon"
              className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full border-background bg-muted"
              onClick={() => toast({ title: 'Upload avatar', description: 'Avatar upload will be available when connected to backend.' })}
            >
              <Camera className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl font-semibold text-foreground">{user?.name ?? 'User'}</h2>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <div className="mt-2 flex flex-wrap justify-center gap-1.5 sm:justify-start">
              {(user?.roles ?? []).map((role) => (
                <Badge key={role} variant="secondary" className="capitalize">
                  {role.replace('_', ' ')}
                </Badge>
              ))}
            </div>
            <p className="mt-2 flex items-center justify-center gap-1.5 text-sm text-muted-foreground sm:justify-start">
              <Building2 className="h-3.5 w-3.5" />
              {user?.tenant?.name ?? 'Organization'}
            </p>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/profile/settings')}>
              <Settings className="h-4 w-4" /> Settings
            </Button>
            {!editing ? (
              <Button size="sm" className="gap-1.5" onClick={() => setEditing(true)}>
                <Edit2 className="h-4 w-4" /> Edit
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={handleCancel}>
                  <X className="h-4 w-4" /> Cancel
                </Button>
                <Button size="sm" className="gap-1.5" onClick={handleSave}>
                  <Save className="h-4 w-4" /> Save
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Personal details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Personal Details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {([
            { key: 'name', label: 'Full Name', icon: Edit2 },
            { key: 'email', label: 'Email', icon: Mail },
            { key: 'phone', label: 'Phone', icon: Phone },
            { key: 'title', label: 'Job Title', icon: Briefcase },
            { key: 'department', label: 'Department', icon: Building2 },
          ] as const).map(({ key, label, icon: Icon }) => (
            <div key={key} className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Icon className="h-3.5 w-3.5" /> {label}
              </Label>
              {editing ? (
                <Input
                  value={form[key]}
                  onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              ) : (
                <p className="text-sm font-medium text-foreground">{form[key]}</p>
              )}
            </div>
          ))}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" /> Joined
            </Label>
            <p className="text-sm font-medium text-foreground">January 15, 2024</p>
          </div>
        </CardContent>
      </Card>

      {/* Skills */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Skills & Certifications</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {MOCK_SKILLS.map((skill) => (
            <Badge key={skill} variant="outline" className="px-3 py-1">
              {skill}
            </Badge>
          ))}
        </CardContent>
      </Card>

      {/* Achievements */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {MOCK_ACHIEVEMENTS.map((a) => (
          <Card key={a.label}>
            <CardContent className="flex items-center gap-4 p-5">
              <div className={`rounded-lg bg-muted p-2.5 ${a.color}`}>
                <a.icon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{a.value}</p>
                <p className="text-xs text-muted-foreground">{a.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
